import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Download,
  Mail,
  Calendar,
  User,
  Briefcase,
  FileText,
  Clock,
  MapPin,
  Building,
  DollarSign,
  Award,
  Star,
  CheckCircle,
  XCircle,
  MessageSquare,
} from "lucide-react";
import {
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import Button from "../../components/common/Button";
import adminApi from "../../api/admin";
import { formatDateForDisplay } from "../../utils/helpers";

const AdminJobApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [feedback, setFeedback] = useState("");

  const {
    data: application,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["job-application-details", id],
    queryFn: async () => {
      const res = await adminApi.getJobApplicationDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStatusUpdate = (newStatus) => {
    setSelectedStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleFeedbackUpdate = () => {
    setFeedbackDialogOpen(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      // await adminApi.updateApplicationStatus(id, selectedStatus);
      console.log("Updating status to:", selectedStatus);
      setStatusDialogOpen(false);
      // Optionally refetch data
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const confirmFeedbackUpdate = async () => {
    try {
      // await adminApi.updateApplicationFeedback(id, feedback);
      console.log("Updating feedback:", feedback);
      setFeedbackDialogOpen(false);
      setFeedback("");
      // Optionally refetch data
    } catch (error) {
      console.error("Failed to update feedback:", error);
    }
  };

  const downloadDocument = (document) => {
    window.open(document.download_url, "_blank");
  };

  const statusConfig = {
    submitted: { color: "default", label: "Submitted", icon: FileText },
    under_review: { color: "primary", label: "Under Review", icon: Clock },
    shortlisted: { color: "success", label: "Shortlisted", icon: Star },
    interview: { color: "warning", label: "Interview", icon: MessageSquare },
    offer_extended: { color: "info", label: "Offer Extended", icon: Award },
    hired: { color: "success", label: "Hired", icon: CheckCircle },
    rejected: { color: "error", label: "Rejected", icon: XCircle },
    withdrawn: { color: "default", label: "Withdrawn", icon: XCircle },
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            Error loading job application details: {error?.message}
          </p>
          <Button
            onClick={() => navigate("/admin/job-applications")}
            variant="outline"
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Job application not found</p>
          <Button
            onClick={() => navigate("/admin/job-applications")}
            variant="outline"
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  const currentStatus =
    statusConfig[application.status] || statusConfig.submitted;
  const applicant = application.applicant;
  const job = application.job;

  const statusOptions = [
    { value: "under_review", label: "Under Review", color: "primary" },
    { value: "shortlisted", label: "Shortlisted", color: "success" },
    { value: "interview", label: "Interview", color: "warning" },
    { value: "offer_extended", label: "Offer Extended", color: "info" },
    { value: "hired", label: "Hired", color: "success" },
    { value: "rejected", label: "Rejected", color: "error" },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/job-applications")}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">
              Application Details
            </h1>
            <p className="text-secondary-600">
              Manage and review job application
            </p>
          </div>
        </div>
        {/* <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleFeedbackUpdate}
            icon={<MessageSquare className="w-4 h-4 mr-2" />}
          >
            Add Feedback
          </Button>
        </div> */}
      </div>

      {/* Application Overview */}
      <Grid container spacing={3} className="mb-6">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-secondary-900 mb-2">
                    {applicant.full_name}
                  </h2>
                  <p className="text-secondary-600 mb-4">
                    Application for: {job.title}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-600">
                    <div className="flex items-center space-x-1">
                      <Mail className="w-4 h-4" />
                      <span>{applicant.email}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Applied {application.days_since_submission} days ago
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Building className="w-4 h-4" />
                      <span>{job.company_name || "No company"}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mt-4 lg:mt-0">
                  <currentStatus.icon
                    className={`w-5 h-5 text-${currentStatus.color}`}
                  />
                  <Chip
                    label={application.status_display}
                    color={currentStatus.color}
                    size="medium"
                  />
                </div>
              </div>

              {/* Status Actions */}
              <div className="border-t pt-4">
                <Typography
                  variant="subtitle2"
                  className="mb-3 text-secondary-600"
                >
                  Application Statuses:
                </Typography>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((option) => (
                    <MuiButton
                      key={option.value}
                      variant="outlined"
                      size="small"
                      color={option.color}
                      //   onClick={() => handleStatusUpdate(option.value)}
                      disabled={application.status === option.value}
                    >
                      {option.label}
                    </MuiButton>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                Job Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-secondary-600">Location:</span>
                  <span className="font-medium flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {job.location}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Job Type:</span>
                  <span className="font-medium capitalize">
                    {job.job_type?.replace("-", " ")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Experience:</span>
                  <span className="font-medium capitalize">
                    {job.experience_level}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Salary:</span>
                  <span className="font-medium flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {job.salary_min && job.salary_max
                      ? `${job.salary_min} - ${job.salary_max}`
                      : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Urgent:</span>
                  <Chip
                    label={job.is_urgent ? "Yes" : "No"}
                    color={job.is_urgent ? "error" : "default"}
                    size="small"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Content */}
      <Card>
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className="border-b"
            sx={{
              minWidth: "max-content",
              "& .MuiTabs-scrollButtons": {
                "&.Mui-disabled": { opacity: 0.3 },
              },
            }}
          >
            <Tab label="Application Details" />
            <Tab label="Cover Letter" />
            <Tab label="Documents" />
            <Tab label="Screening Answers" />
          </Tabs>
        </div>

        <CardContent className="p-6">
          {tabValue === 0 && (
            <div className="space-y-6">
              {/* Applicant Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                  Applicant Information
                </h3>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">Full Name</span>
                        <span className="font-medium">
                          {applicant.full_name}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">Email</span>
                        <span className="font-medium">{applicant.email}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">
                          Email Verified
                        </span>
                        <Chip
                          label={
                            applicant.is_email_verified
                              ? "Verified"
                              : "Not Verified"
                          }
                          color={
                            applicant.is_email_verified ? "success" : "default"
                          }
                          size="small"
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">Member Since</span>
                        <span className="font-medium">
                          {formatDateForDisplay(applicant.date_joined)}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">Role</span>
                        <span className="font-medium capitalize">
                          {applicant.role}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-secondary-600">
                          Specialization
                        </span>
                        <span className="font-medium">
                          {applicant.specialization || "Not specified"}
                        </span>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>

              {/* Application Timeline */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                  Application Timeline
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Submitted</span>
                    <span className="font-medium">
                      {formatDateForDisplay(application.created_at)} at{" "}
                      {new Date(application.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  {/* <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Last Updated</span>
                    <span className="font-medium">
                      {new Date(application.updated_at).toLocaleDateString()} at{' '}
                      {new Date(application.updated_at).toLocaleTimeString()}
                    </span>
                  </div> */}
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">
                      Days Since Submission
                    </span>
                    <span className="font-medium">
                      {application.days_since_submission} days
                    </span>
                  </div>
                </div>
              </div>

              {/* Feedback Section */}
              {application.feedback && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                    Internal Feedback
                  </h3>
                  <Card className="bg-gray-50">
                    <CardContent className="p-4">
                      <p className="text-secondary-700">
                        {application.feedback}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {tabValue === 1 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                Cover Letter
              </h3>
              {application.cover_letter ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="prose max-w-none">
                      <p className="text-secondary-700 leading-relaxed whitespace-pre-wrap">
                        {application.cover_letter}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center py-8 text-secondary-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No cover letter provided</p>
                </div>
              )}
            </div>
          )}

          {tabValue === 2 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                Application Documents
              </h3>
              {application.documents && application.documents.length > 0 ? (
                <div className="space-y-3">
                  {application.documents.map((document) => (
                    <Card
                      key={document.id}
                      className="hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-8 h-8 text-blue-500" />
                            <div>
                              <p className="font-semibold text-secondary-900">
                                {document.name}
                              </p>
                              <p className="text-sm text-secondary-600">
                                {document.document_type.toUpperCase()} •{" "}
                                {(document.size / 1024).toFixed(1)} KB •{" "}
                                {new Date(
                                  document.uploaded_at
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDocument(document)}
                            icon={<Download className="w-4 h-4 mr-2" />}
                          >
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-secondary-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No documents uploaded</p>
                </div>
              )}
            </div>
          )}

          {tabValue === 3 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                Screening Questions
              </h3>
              {job.screening_questions && job.screening_questions.length > 0 ? (
                <div className="space-y-4">
                  {job.screening_questions.map((question, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-secondary-900">
                              {question.question}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <Chip
                                label={question.type}
                                size="small"
                                variant="outlined"
                              />
                              {question.essential && (
                                <Chip
                                  label="Essential"
                                  color="error"
                                  size="small"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-secondary-700">
                            {application.answers?.additional_information ||
                              "No answer provided"}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-secondary-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No screening questions for this job</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>Update Application Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the application status to{" "}
            <strong>
              {statusOptions.find((opt) => opt.value === selectedStatus)?.label}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setStatusDialogOpen(false)}>
            Cancel
          </MuiButton>
          <MuiButton onClick={confirmStatusUpdate} variant="contained">
            Confirm Update
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Internal Feedback</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Feedback Notes"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Enter internal feedback about this application..."
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setFeedbackDialogOpen(false)}>
            Cancel
          </MuiButton>
          <MuiButton onClick={confirmFeedbackUpdate} variant="contained">
            Save Feedback
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminJobApplicationDetailPage;
